<template>
  <div class="support-page modern-scrollbar">
    <div class="support-inner">

      <!-- Left: Selector + Form -->
      <div class="support-main">

        <!-- Service Selector -->
        <div class="section-block">
          <h2 class="section-heading">Need help?</h2>
          <p class="section-sub">Please let us know how we can assist by selecting the commodity or service you require help with below:</p>

          <!-- Skeleton grid while sources load, so tiles don't pop in / re-sort -->
          <div v-if="servicesLoading" class="elements-grid">
            <div v-for="n in 24" :key="`skeleton-${n}`" class="element-tile element-tile--skeleton" />
          </div>

          <!-- Merged grid: element tiles + icon service tiles together -->
          <div v-else-if="allServices.length" class="elements-grid">
            <template v-for="sub in allServices">
              <!-- Element tile -->
              <div
                  v-if="sub.element"
                  :key="`element-${sub.id}`"
                  class="element-tile"
                  :class="{ 'element-tile--active': selectedSub?.id === sub.id }"
                  @click="selectSub(sub)"
                  :title="sub.subscriptionName"
              >
                <span class="el-symbol-box">
                  <span class="el-symbol" :style="{ fontSize: symbolFontSize(sub.element.code) }">{{ sub.element.code }}</span>
                </span>
                <span class="el-name">{{ sub.element.name }}</span>
              </div>

              <!-- Icon service tile (styled to match element tiles) -->
              <div
                  v-else
                  :key="`icon-${sub.id}`"
                  class="element-tile element-tile--icon"
                  :class="{ 'element-tile--active': selectedSub?.id === sub.id }"
                  @click="selectSub(sub)"
                  :title="sub.subscriptionName"
              >
                <img :src="sub.iconUrl" :alt="sub.subscriptionName" class="service-icon-img" />
                <span class="el-name">{{ sub.subscriptionName }}</span>
              </div>
            </template>
          </div>

          <!-- Subscribed Pricing Services Supply Chains -->
          <div v-if="!servicesLoading && supplyChainServices.length" class="supply-chains-block">
            <h6 class="sub-section-heading">Supply Chains</h6>
            <div class="elements-grid">
              <div
                  v-for="sub in supplyChainServices"
                  :key="`supply-chain-${sub.id}`"
                  class="element-tile"
                  :class="{ 'element-tile--active': selectedSub?.id === sub.id }"
                  @click="selectSub(sub)"
                  :title="sub.subscriptionName"
              >
                <span class="el-symbol-box">
                  <span class="el-symbol" :style="{ fontSize: symbolFontSize(sub.element.code) }">{{ sub.element.code }}</span>
                </span>
                <span class="el-name">{{ sub.element.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="divider" />

        <!-- Form -->
        <div class="section-block">
          <h2 class="section-heading">Analyst Access</h2>
          <p class="section-sub">Please let us know how we can assist you further:</p>

          <div class="form-card">
            <div class="user-details-block">
              <h6 class="details-label">Your Details:</h6>
              <table class="details-table">
                <tbody>
                  <tr>
                    <td class="details-key">Name:</td>
                    <td class="details-val">{{ SUBSCRIPTION_INFO?.fullname }}</td>
                  </tr>
                  <tr v-if="selectedSub">
                    <td class="details-key">Selected:</td>
                    <td class="details-val selected-highlight">{{ selectedSub.subscriptionName }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="field-row">
              <div class="phone-group">
                <NSelect
                    v-model:value="contactNumberCountry"
                    :options="mobileList"
                    :theme-overrides="inputTheme"
                    filterable
                    placeholder="Select Country"
                    class="custom-form phone-select"
                    @update:value="handleUpdateContactNumber"
                    style="width: 50%"
                />
                <NInput
                    v-model:value="input.contactNumber"
                    type="text"
                    :theme-overrides="contactTheme"
                    class="form-control custom-form"
                    placeholder="Contact number"
                    style="width: 50%"
                />
              </div>
            </div>

            <div class="field-row">
              <textarea
                  v-model="input.message"
                  class="form-control message-form custom-form"
                  placeholder="*Your message"
                  rows="5"
              />
            </div>

            <div class="form-actions">
              <button class="btn-send" @click="sendToMarketing">Send Message</button>
              <p class="required-note">* These fields are required.</p>
            </div>
          </div>
        </div>

      </div>

      <!-- Right panel -->
      <div class="support-side">
        <div class="side-card">
          <div class="side-header">
            <div class="side-icon"><i class="fas fa-headset"></i></div>
            <h3 class="side-title">Our Analyst Team</h3>
          </div>
          <p class="side-body">
            Select a commodity or service above to direct your query to the right specialist.
            Our analysts are on hand to provide in-depth market support.
          </p>
          <div class="side-divider" />
          <div class="side-stat" v-if="activeSubscriptionsCount">
            <span class="stat-num">{{ activeSubscriptionsCount }}</span>
            <span class="stat-label">Active Subscription{{ activeSubscriptionsCount !== 1 ? 's' : '' }}</span>
          </div>
          <transition name="fade-up">
            <div v-if="selectedSub" class="selected-badge">
              <i class="fas fa-check-circle"></i>
              <span>{{ selectedSub.subscriptionName }}</span>
            </div>
          </transition>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { NSelect, NInput } from "naive-ui";
import { SUBSCRIPTION_INFO, getSubscriptionInfo, SUBSCRIPTION_ELEMENTS_OPTS } from "../../composables/subscription";
import { getUserFiles, MONTHLY_SERVICES, COST_SERVICES } from "../../composables/files/list";
import { sendInquiry, RESPONSE, LEAD_TYPE } from "../../composables/marketSupport/inquiry";
import { COUNTRIES_LIST } from "../../utils/generic.ts";
import { BASE_ELEMENT_INFORMATION, getBaseElementInformation } from "../../composables/profiles/asset";
const contactTheme = {
  borderHover: '1px solid #213ce5',
  borderFocus: '1px solid #213ce5',
  boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
  fontFamily: "'GilroyMedium', sans-serif",
};
const inputTheme = {
  peers: {
    InternalSelection: {
      borderHover: '1px solid #213ce5',
      borderFocus: '1px solid #213ce5',
      borderActive: '1px solid #213ce5',
      boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowActive: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowHover: 'none',
      caretColor: '#213ce5',
      arrowColorActive: '#213ce5',
      arrowColorHover: '#213ce5',
      color: '#fff',
      colorActive: '#ffffff',
      colorFocus: '#ffffff',
      fontFamily: "'GilroyMedium', sans-serif",
    },
    InternalSelectMenu: {
      optionColorActive: 'rgba(33, 60, 229, 0.08)',
      optionColorActivePending: 'rgba(33, 60, 229, 0.12)',
      optionColorPending: 'rgba(33, 60, 229, 0.05)',
      optionTextColorActive: '#213ce5',
      optionTextColorChildActive: '#213ce5',
      optionCheckColor: '#213ce5',
      actionDividerColor: '#213ce5',
      loadingColor: '#213ce5',
      scrollbarColor: 'rgba(33, 60, 229, 0.3)',
      scrollbarColorHover: 'rgba(33, 60, 229, 0.5)',
      fontFamily: "'GilroyMedium', sans-serif",
    }
  }
};
const contactNumberCountry = ref(null);

// ─── Element lookup ───────────────────────────────────────────────────────────
// Build map: lowercase element name → { name, code, iconUrl }
const elementMap = computed(() =>
  (BASE_ELEMENT_INFORMATION.value || []).reduce((acc, el) => {
    acc[el.name.toLowerCase()] = {
      name: el.name,
      code: el.code,
      iconUrl: el.iconUrl,
    };
    return acc;
  }, {})
);

// Build map: element code → { name, code, iconUrl }, for sources that already
// give us a code (dataElements, SUBSCRIPTION_ELEMENTS_OPTS) rather than a
// subscription-name string that needs fuzzy matching.
const elementByCode = computed(() =>
  (BASE_ELEMENT_INFORMATION.value || []).reduce((acc, el) => {
    acc[el.code] = { name: el.name, code: el.code, iconUrl: el.iconUrl };
    return acc;
  }, {})
);

/**
 * Scales the element symbol's font size down as its character count grows,
 * so multi-letter codes (e.g. "Cathode") still fit within the 64px tile
 * without overlapping the name label below.
 */
const symbolFontSize = (code) => {
  const len = code?.length || 1;
  if (len <= 2) return '1.5rem';
  if (len === 3) return '1.15rem';
  if (len === 4) return '0.95rem';
  if (len <= 6) return '0.75rem';
  if (len <= 9) return '0.6rem';
  return '0.52rem'; // Scale down for longer codes such as EV_BATTERY and STEEL_ALLOY
};

/**
 * Returns matching element metadata if a known element name appears at the
 * start of the subscription name (word-boundary prefix, plural-tolerant),
 * e.g. "Antimony Trial" and "Cathode & precursor" both match.
 */
const matchElement = (subscriptionName) => {
  if (!subscriptionName) return null;
  const lower = subscriptionName.toLowerCase().trim();
  const matched = Object.entries(elementMap.value)
      .filter(([name]) => {
        const regex = new RegExp(`^${name}s?(\\b|$)`, 'i');
        return regex.test(lower);
      })
      .sort((a, b) => b[0].length - a[0].length)[0];
  return matched ? matched[1] : null;
};

// ─── Computed service lists ───────────────────────────────────────────────────
// allServices consolidates every source of "commodities/services this user
// has access to" across the app into a single, deduplicated tile list:
//  - SUBSCRIPTION_INFO.dataElements            → Data Dashboards + Market Analysis access
//  - SUBSCRIPTION_ELEMENTS_OPTS                → Asset Profiles element access
//  - SUBSCRIPTION_INFO.pricingServicesElements → Pricing Services commodities
//  - COST_SERVICES / MONTHLY_SERVICES          → subscribed services (getUserFiles)
// Each entry is reduced to a unique key (matched element code, or the first
// word of its name/subscriptionName when no element match exists) so the
// same commodity never renders twice, regardless of which source it came from.
const allServices = computed(() => {
  const deduped = new Map();

  const addTile = ({ key, subscriptionName, element, iconUrl }) => {
    if (!key || deduped.has(key)) return;
    deduped.set(key, { id: key, subscriptionName, element, iconUrl });
  };

  for (const el of SUBSCRIPTION_INFO?.dataElements || []) {
    const meta = elementByCode.value[el.code] || { name: el.name, code: el.code, iconUrl: null };
    addTile({ key: meta.code, subscriptionName: meta.name, element: meta });
  }

  for (const opt of SUBSCRIPTION_ELEMENTS_OPTS.value || []) {
    if (opt.type !== 'ELEMENT') continue;
    const meta = elementByCode.value[opt.value] || { name: opt.label, code: opt.value, iconUrl: null };
    addTile({ key: meta.code, subscriptionName: meta.name, element: meta });
  }

  // Pricing Services commodities (non-supply-chain items)
  for (const el of SUBSCRIPTION_INFO?.pricingServicesElements || []) {
    if (el.isSupplyChain) continue;
    const meta = elementByCode.value[el.code] || { name: el.name, code: el.code, iconUrl: null };
    addTile({ key: meta.code, subscriptionName: meta.name, element: meta });
  }

  const services = [
    ...(MONTHLY_SERVICES.value || []),
    ...(COST_SERVICES.value || []),
  ];
  for (const s of services) {
    const element = matchElement(s.subscriptionName);
    const key = element?.code || s.subscriptionName?.toLowerCase().trim().split(/\s+/)[0];
    addTile({ key, subscriptionName: s.subscriptionName, element, iconUrl: s.iconUrl });
  }

  // Safety net: some sources key their tile differently from an existing
  // element tile even though they share the same leading word (e.g.
  // "Niobium" and "Niobium Cost Tracker"). Collapse those, preferring the
  // standalone element tile over the icon-service variant.
  const byFirstWord = new Map();
  for (const tile of deduped.values()) {
    const name = tile.element?.name || tile.subscriptionName || '';
    const firstWord = name.toLowerCase().trim().split(/\s+/)[0];
    const existing = byFirstWord.get(firstWord);
    if (!existing || (!existing.element && tile.element)) {
      byFirstWord.set(firstWord, tile);
    }
  }

  return [...byFirstWord.values()].sort((a, b) => {
    const nameA = a.element?.name || a.subscriptionName || '';
    const nameB = b.element?.name || b.subscriptionName || '';
    return nameA.localeCompare(nameB);
  });
});

// Subscribed Pricing Services supply chains
const supplyChainServices = computed(() => {
  const list = [];
  const seen = new Set();

  for (const el of SUBSCRIPTION_INFO?.pricingServicesElements || []) {
    if (!el.isSupplyChain) continue;
    if (seen.has(el.code)) continue;
    seen.add(el.code);

    const meta = elementByCode.value[el.code] || { name: el.name, code: el.code, iconUrl: null };
    list.push({
      id: meta.code,
      subscriptionName: meta.name,
      element: meta,
      isSector: true,
    });
  }

  return list.sort((a, b) => {
    const nameA = a.element?.name || a.subscriptionName || '';
    const nameB = b.element?.name || b.subscriptionName || '';
    return nameA.localeCompare(nameB);
  });
});

const activeSubscriptionsCount = computed(
  () => allServices.value.length + supplyChainServices.value.length
);

// ─── Form state ───────────────────────────────────────────────────────────────
const selectedSub = ref(null);

const input = reactive({
  firstname: null,
  lastname: null,
  company: null,
  country: null,
  email: null,
  contactNumberPrefix: null,
  contactNumber: null,
  interests: null,
  sectors: null,
  message: null,
  leadType: null,
  product: "Support Page",
  description: null,
  subExpiry: null,
  isMultiple: false,
  parentSubscriptionPackages: null,
});

const mobileList = COUNTRIES_LIST.map((nth) => ({
  label: `(${nth.mobileCode}) ${nth.name}`,
  value: nth.code,
  mobile: nth.mobileCode,
}));

const countriesList = COUNTRIES_LIST.map((nth) => ({
  label: nth.name,
  value: nth.code,
  mobile: nth.mobileCode,
}));

// ─── Methods ──────────────────────────────────────────────────────────────────
const selectSub = (sub) => {
  if (selectedSub.value?.id === sub.id) {
    selectedSub.value = null;
    input.description = null;
    input.product = "Support Page";
    input.message = null;
    input.sectors = null;
    return;
  }

  selectedSub.value = sub;
  input.description = sub.subscriptionName;
  input.product = `Support Page — ${sub.subscriptionName}`;
  input.sectors = sub.isSector ? [sub.subscriptionName] : null;
  input.message = `I need help with: ${sub.subscriptionName}\n\n`;
};
const handleUpdateContactNumber = (value, opt) => {
  contactNumberCountry.value = value;
  input.contactNumberPrefix = `${opt.value} (${opt.mobile})`;
  input.country = countriesList.find((c) => c.value === opt.value)?.label || null;
};

const sendToMarketing = async () => {
  const isReady = input.contactNumberPrefix && input.contactNumber && input.message;

  if (!isReady) {
    window.$message.error("Please complete all required fields before submitting.");
    return;
  }

  const info = SUBSCRIPTION_INFO.value || SUBSCRIPTION_INFO;
  input.firstname = info?.firstname || info?.fullname?.split(" ")[0] || "";
  input.lastname  = info?.lastname  || info?.fullname?.split(" ").slice(1).join(" ") || "";
  input.email     = info?.email || "";
  input.company   = info?.company || info?.companyName || "";
  input.leadType  = LEAD_TYPE.value?.INQUIRY;

  if (selectedSub.value) {
    input.description = selectedSub.value.subscriptionName;
    input.interests   = selectedSub.value.elements || [selectedSub.value.subscriptionName];
    input.sectors     = selectedSub.value.isSector ? [selectedSub.value.subscriptionName] : null;
  }

  await sendInquiry(input, "Analyst Access");
};

// ─── Lifecycle ────────────────────────────────────────────────────────────────
const servicesLoading = ref(true);

onMounted(async () => {
  await Promise.all([
    MONTHLY_SERVICES.value?.length ? null : getUserFiles(),
    (SUBSCRIPTION_INFO?.fullname && SUBSCRIPTION_INFO?.pricingServicesElements) ? null : getSubscriptionInfo(false),
    BASE_ELEMENT_INFORMATION.value?.length ? null : getBaseElementInformation(),
  ]);
  servicesLoading.value = false;
});
</script>

<style lang="scss" scoped>
.phone-group {
  display: flex;
  width: 100%;

  .phone-select {
    width: 50%;
    flex-shrink: 0;
  }

  .phone-input {
    width: 50%;
    padding: 8px 12px;
    font-size: 14px;
    color: #020202;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-left: 1px solid transparent;
    outline: none;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &::placeholder { color: #718096; }

    &:focus {
      background-color: #fff;
      border-color: #213ce5;
      box-shadow: 0 0 0 2px rgba(33, 60, 229, 0.2);
    }
  }
}
/* ── Layout ────────────────────────────────────────────────────────────── */
.support-page {
  height: calc(100vh - 70px);
  width: 100%;
  padding: 0.5rem;
  background: #f8f9fa;
}

.support-inner {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 0.5rem;
  margin: 0 auto;
  align-items: start;
}

.support-main {
  display: flex;
  flex-direction: column;
}

.section-block {
  background: #fff;
  padding: 1.25rem 1.5rem;
}

.divider {
  height: 0.5rem;
  background: #f8f9fa;
}

.section-heading {
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.2rem;
}

.section-sub {
  font-size: 0.8125rem;
  color: #495057;
  margin: 0 0 1rem;
}

/* ── Supply Chains Block ──────────────────────────────────────────────── */
.supply-chains-block {
  margin-top: 1.25rem;
}

.sub-section-heading {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.5rem;
}

/* ── Merged element + icon service tiles ──────────────────────────────── */
.elements-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

@keyframes element-tile-skeleton {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.element-tile {
  width: 64px;
  height: 68px;
  background: #213ce5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid transparent;
  padding: 0.2rem;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 60, 229, 0.35);
    background: #1a32c8;
  }

  &--skeleton {
    cursor: default;
    pointer-events: none;
    background: linear-gradient(90deg, #e9ecf7 25%, #f3f4fa 37%, #e9ecf7 63%);
    background-size: 400% 100%;
    animation: element-tile-skeleton 1.4s ease infinite;
  }

  &--active {
    background: #fff !important;
    border-color: #213ce5;
    box-shadow: 0 0 0 3px rgba(33, 60, 229, 0.18);
    transform: translateY(-2px);

    .el-number, .el-symbol, .el-name { color: #213ce5 !important; }
  }

  /* Icon-service variant — same footprint as an element tile, but shows
     an icon image instead of number/symbol */
  &--icon {
    .service-icon-img {
      width: 28px;
      height: 28px;
      object-fit: contain;
      margin-bottom: 4px;
    }

    .el-name {
      max-width: 58px;
    }
  }
}

.el-number {
  font-size: 0.52rem;
  color: rgba(255,255,255,0.75);
  font-weight: 600;
  align-self: flex-start;
  padding-left: 3px;
  line-height: 1;
}

.el-symbol-box {
  min-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-symbol {
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
  max-width: 58px;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: center;
}

.el-name {
  font-size: 0.44rem;
  color: rgba(255,255,255,0.85);
  text-align: center;
  max-width: 60px;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
}

/* ── Form ──────────────────────────────────────────────────────────────── */
.form-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.user-details-block { padding: 0.25rem 0; }

.details-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #000;
  margin: 0 0 0.4rem;
}

.details-table {
  font-size: 0.8125rem;
  width: 100%;
  td { padding: 0.15rem 0.5rem 0.15rem 0; }
}

.details-key { color: #718096; width: 80px; }
.details-val { color: #000; font-weight: 500; }
.selected-highlight { color: #213ce5; font-weight: 600; }

.field-row { width: 100%; }

.custom-form {
  display: block;
  width: 100%;
  height: calc(2em + 1rem + 2px);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #020202;
  background-color: #fff !important;   /* was #f0f2f5 */
  border-radius: 0 !important;
  font-family: 'GilroyMedium', sans-serif;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    background-color: #fff;
    border-color: #213ce5;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(33, 60, 229, 0.25);
  }

  &::placeholder { color: #718096; opacity: 1; }
}

.message-form {
  height: auto !important;
  background-color: #fff !important;   /* was #f0f2f5 */
  border: 1px solid #ced4da;
  border-radius: 0 !important;
  font-family: 'GilroyMedium', sans-serif;
  font-size: 14px;
  resize: vertical;

  &:focus {
    background-color: #fff !important;
    border-color: #213ce5 !important;
    box-shadow: 0 0 0 0.2rem rgba(33, 60, 229, 0.25) !important;
    outline: 0;
  }
}

.form-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding-top: 0.25rem;
}

.btn-send {
  background-color: #213ce5;
  color: #fff;
  border: none;
  border-radius: 0 !important;
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background-color: #1a32c8; }
}

.required-note {
  font-size: 0.8rem;
  color: #718096;
  margin: 0;
}

/* ── Right side panel ──────────────────────────────────────────────────── */
.support-side {
  position: sticky;
  top: 0;
}

.side-card {
  background: #213ce5;
  padding: 1.5rem 1.25rem;
  color: #fff;
}
.side-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.side-icon {
  width: 44px;
  height: 44px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.side-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.side-body {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.85);
  line-height: 1.5;
  margin: 0;
}

.side-divider {
  height: 1px;
  background: rgba(255,255,255,0.2);
  margin: 1rem 0;
}

.side-stat {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.stat-num {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.75);
}

.selected-badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;

  i { color: #10B981; font-size: 0.9rem; }
}

/* transition */
.fade-up-enter-active, .fade-up-leave-active { transition: all 0.25s ease; }
.fade-up-enter-from, .fade-up-leave-to { opacity: 0; transform: translateY(6px); }

/* ── Naive UI overrides ─────────────────────────────────────────────────── */
:deep(.phone-select) {
  .n-base-selection {
    height: 100%;
    border: 1px solid #ced4da !important;
    border-radius: 0 !important;
    font-size: 14px;
    font-family: 'GilroyMedium', sans-serif;
    background-color: #fff;
  }

  .n-base-selection-label {
    height: 100%;
    color: #020202;
    background-color: #fff;
  }

  .n-base-selection-placeholder__inner {
    color: #718096;
  }
  .n-base-selection-placeholder {
    color: #718096;
  }
}
* { border-radius: 0 !important; }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .support-inner {
    grid-template-columns: 1fr;
  }
  .support-side {
    position: static;
    order: -1;
  }
}
</style>